import styled from 'styled-components'
import { Body } from '@shipt/design-system-typography'
import { ProductCardColor } from '@/features/shop/components/ProductCard/colors/ProductCardColor'
import { type VariationParent } from '@/features/shop/services/Product/variationTypes'
import { SkeletonConsumer } from '@/features/shared/components/SkeletonLoader'
import { useExperiment } from '@/features/shared/services/Experiments/hooks'
import { ExperimentType } from '@shared/types/experiments'

type Props = {
  variationParent: VariationParent
}

// Total number of colors to show on the search results
const maxColors = 3

export const ProductVariationInfo = ({ variationParent }: Props) => {
  const { active } = useExperiment(ExperimentType.VARIATIONS_ENHANCED_PROPERTIES, {
    autoTrack: true,
  })

  const { total_colors, variation_colors } = variationParent
  const colorsWithoutSwatches =
    variation_colors
      ?.slice(0, maxColors)
      .filter(({ swatch_url, swatch_color }) => !swatch_url && !swatch_color).length || 0
  const possibleDisplaySlots = Math.min(total_colors, maxColors)
  const remainingColorCount = Math.max(
    total_colors - possibleDisplaySlots + colorsWithoutSwatches,
    0,
  )
  const isSingular = remainingColorCount === 1
  const hasVisibleSwatches = remainingColorCount < total_colors

  let displayText = ''
  if (active && !hasVisibleSwatches) {
    displayText = 'Options available'
  } else if (remainingColorCount) {
    displayText = `${hasVisibleSwatches ? '+ ' : ''}${remainingColorCount} color${isSingular ? '' : 's'}`
  }

  return (
    <SkeletonConsumer width={30} height={14}>
      <InfoContainer>
        {variation_colors?.slice(0, maxColors).map((variationColor) => (
          <ProductCardColor key={variationColor.color} variationColor={variationColor} />
        ))}
        <Body size="sm" variant="secondary">
          {displayText}
        </Body>
      </InfoContainer>
    </SkeletonConsumer>
  )
}

const InfoContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`
